import Vue from 'vue';
import VueRouter from 'vue-router';
import firebase from 'firebase/app';
import 'firebase/auth';
import { store } from '../store/index';

Vue.use(VueRouter);

// source: https://alligator.io/vuejs/lazy-loading-vue-cli-3-webpack/
function loadView(view) {
  return () =>
    // route level code-splitting
    // this generates a separate chunk (view-[ViewName].[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`);
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: loadView('HomeView'),
    meta: {
      title: 'Home - poi-hub.com',
    },
  },
  {
    path: '/pois/map',
    name: 'pois-map',
    component: loadView('PoisView'),
    meta: {
      authorize: ['admin'],
      requiresAuth: true,
      title: 'POIs - poi-hub.com',
    },
  },
  {
    path: '/pois/list',
    name: 'pois-list',
    component: loadView('PoisListView'),
    meta: {
      // ToDo: also let anonymous users access the view (limited to the most recent 10 records)
      title: 'POIs - poi-hub.com',
    },
  },
  {
    path: '/pois/validate',
    name: 'pois-validate',
    component: loadView('PoisValidate'),
    meta: {
      authorize: ['validator'],
      requiresAuth: true,
      title: 'POIs Validate - poi-hub.com',
    },
  },
  {
    path: '/poi/add',
    name: 'poi-add',
    component: loadView('PoisAddView'),
    meta: {
      authorize: ['contributor'],
      requiresAuth: true,
      title: 'POIs Add - poi-hub.com',
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: loadView('ProfileView'),
    meta: {
      requiresAuth: true,
      title: 'Profile - poi-hub.com',
    },
  },
  // this has to stay at the end of other subroutes like pois/validate (otherwise they render this)
  {
    path: '/pois/:id',
    name: 'poi',
    component: loadView('PoiView'),
    meta: {
      requiresAuth: true,
      title: 'POI - poi-hub.com',
    },
  },
  // ------ auth views ------
  {
    path: '/login',
    name: 'login',
    component: loadView('LoginView'),
    meta: {
      title: 'Login - poi-hub.com',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      title: 'Logout - poi-hub.com',
    },
    beforeEnter: (to, from, next) => {
      firebase
        .auth()
        .signOut()
        .then(() => {
          next('/');
        });
    },
  },
  {
    path: '/register',
    name: 'register',
    component: loadView('Register'),
    meta: {
      title: 'Register - poi-hub.com',
      // temporarily disabled
      requiresAuth: true,
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  // never trust clients :) -> get the user from firebase (instead of the store)
  //const authenticatedUser = firebase.auth().currentUser;
  // the user has a profile attached... get user from store... (otherwise roles can not be checked...)
  const authenticatedUser = await store.getters['auth/user'];

  // get required authorizations from meta
  // source: https://jasonwatmore.com/post/2019/03/08/vuejs-role-based-authorization-tutorial-with-example
  //const { authorize } = to.meta;
  const authorize = to.meta?.authorize ? to.meta.authorize : [];

  if (requiresAuth || authorize.length) {
    if (!authenticatedUser.loggedIn) {
      // not logged in -> redirect to login page with the return url
      return next({ path: '/login', query: { returnUrl: to.path } });
    }

    // get user roles from profile
    const userRoles = authenticatedUser?.data?.profile?.roles ? authenticatedUser.data.profile.roles : [];
    // check if user roles satisfies the route authorization
    // source: https://stackoverflow.com/a/39893636
    if (authorize.length && !authorize.some(r => userRoles.includes(r))) {
      // role not authorised so redirect to home page
      return next({ path: '/' });
    }
  }

  next();
});

export default router;
